import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Loader2Icon, AlertCircleIcon } from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import { AgentService } from "../../services/AgentService";

interface AgentCreateModalProps {
  open: boolean;
  onClose: () => void;
  onAgentCreated: () => void;
  applicationId?: number;
}

const AgentCreateModal: React.FC<AgentCreateModalProps> = ({
  open,
  onClose,
  onAgentCreated,
  applicationId,
}) => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [formData, setFormData] = useState({
    agent_name: "",
    description: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agent_name.trim()) {
      setError("Agent name is required");
      return;
    }

    if (!applicationId || !apiKey) {
      setError("Missing application or authentication");
      return;
    }

    setIsLoading(true);
    setError("");

    const agentData = {
      application_id: applicationId,
      agent_name: formData.agent_name.trim(),
      description: formData.description.trim() || null,
      meta: {},
      state: "active",
    };

    AgentService.createAgent(agentData, setIsLoading, apiKey).subscribe(
      () => {
        handleClose();
        onAgentCreated();
      },
      (error: any) => {
        console.error("Error creating agent:", error);
        setIsLoading(false);
        setError(error.message || "Failed to create agent");
      }
    );
  };

  const handleClose = () => {
    setFormData({ agent_name: "", description: "" });
    setError("");
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Agent</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <AlertCircleIcon className="w-4 h-4 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="agent_name">
              Agent Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="agent_name"
              placeholder="Enter agent name"
              value={formData.agent_name}
              onChange={(e) => handleInputChange("agent_name", e.target.value)}
              maxLength={100}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Optional description for this agent"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              maxLength={500}
              rows={3}
              disabled={isLoading}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !formData.agent_name.trim()}>
              {isLoading && <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />}
              Create Agent
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AgentCreateModal; 